import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Tag } from 'antd';
import { useParams, useHistory, useLocation, withRouter } from 'react-router-dom';
import { getDatasetLabels, transformLabels } from '../../../lib/labels';
import useGlobal from '../../../store/Store';
import {
  setDatasetFileProperties,
  setDatasetFileAnnotations,
  getDatasetFileVersion,
  getDatasetFileVersions,
  getDatasetVersionById,
  getDatasetFileVersionPage,
} from '../../../lib/api/dataset-api';
import ResultSideBar from './ResultSideBar';
import DatasetMenuSideBar from './DatasetMenuSidebar';
import { PdfContainer } from '../../../common/pdf/PdfContainer';
import { DatasetFileVersion, DatasetFilters, DatasetVersion } from '../../../types/dataset';
import { Label } from '../../../types/label';
import { PdfFile, Pagination } from '../../../types';
import { reformDatasetFileToPdfFile } from '../../../lib/util/reform';
import { TopBar } from '../../../components/layout/TopBar';
import { DetailContainer } from '../../../components/layout/DetailContainer';
import { useGetDatasetFiles, useGetProjectAndDataset } from '../../../lib/hooks';
import {
  createQueryStringFirstTimeForPageFetch,
  initialFilterValues,
  renderFilterString,
  setFiltersOnMount,
} from '../Filters';
import { Spinner } from '../../../components/loading/Spinner';
import { Section } from '../../../components/Section';
import { CustomSider } from '../../../components/layout/CustomSider';

const { Content } = Layout;

const defaultPaging = {
  pageSize: 10,
  page: 1,
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const DatasetFileOverview = () => {
  let history = useHistory();
  const location = useLocation();
  const query = useQuery();

  const { projectId, datasetId, fileId } = useParams() as { projectId: string; datasetId: string; fileId: string };

  const [datasetFileVersion, setDatasetFileVersion] = useState<null | DatasetFileVersion>(null);
  const [fileVersionloading, setFileVersionLoading] = useState<boolean>(false);
  const [datasetVersion, setDatasetVersion] = useState<DatasetVersion>();
  const [datasetVersionId, setDatasetVersionId] = useState<string>();

  const [pdfFile, setPdfFile] = useState<PdfFile | null>();

  const { dataset, loading } = useGetProjectAndDataset(projectId, datasetId);

  // Check if this datasetFile is from the latest version of the dataset we're working in
  // -> if not, display warning message to user

  const notOnLatestVersionAlert = useMemo(() => {
    if (datasetVersion?.version_id !== dataset?.latestVersion.version_id) {
      return true;
    } else {
      return false;
    }
  }, [datasetVersion, dataset]);

  const [filters, setFilters] = useState<DatasetFilters>(initialFilterValues);
  const [queryString, setQueryString] = useState<string>('');
  const [paginationObject, setPaginationObject] = useState<Pagination | null>();

  const [globalState, globalActions] = useGlobal();

  const { datasetFiles, filterItemsCount, filesLoading, refetchFiles } = useGetDatasetFiles(
    datasetVersionId || '',
    queryString,
    paginationObject || null
  );

  const [datasetFilesLength, setDatasetFilesLength] = useState<number>(datasetFiles.length);

  useEffect(() => {
    setDatasetFilesLength(datasetFiles.length);
  }, [datasetFiles]);

  //ONMOUNT: get the query parameters and set filters on these values
  useEffect(() => {
    setFiltersOnMount(query, setFilters, setPaginationObject);
    //we dont want query in dependency arry => infinite loop:
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getPage = async () => {
      if (fileId && datasetVersionId) {
        const queryString = createQueryStringFirstTimeForPageFetch(query);
        const page = await getDatasetFileVersionPage(
          fileId,
          datasetVersionId,
          'size=' + defaultPaging.pageSize + queryString
        );
        setPaginationObject(prev => ({ pageSize: prev?.pageSize || defaultPaging.pageSize, page: page }));
      }
    };
    if (!query.get('page')) {
      getPage();
    } else {
      setPaginationObject({
        pageSize: defaultPaging.pageSize,
        page: query.get('page') ? parseInt(query.get('page')!) : defaultPaging.page,
      });
    }
    //query will change itself if it is part of dependency array => infinite loop:
    //eslint-disable-next-line
  }, [datasetVersionId]);

  const fetchData = useCallback(async () => {
    let file;
    setFileVersionLoading(true);
    try {
      file = await getDatasetFileVersion(fileId, true);
    } catch {
      const versions = await getDatasetFileVersions(fileId);
      history.replace(`/projects/${projectId}/datasets/${datasetId}/dataset-file/${versions[0].id}`);
      return;
    }
    const datasetVersion = await getDatasetVersionById(file.dataset_version_id);
    setDatasetVersion(datasetVersion);
    setDatasetVersionId(datasetVersion.version_id);
    globalActions.setTags(file.datasetVersion.tags);
    if (datasetFilesLength) {
      if (file) {
        const labels = getDatasetLabels(file, file.datasetVersion);
        const strippedLabels = transformLabels(labels);
        await setDatasetFileAnnotations(file.id, { fields: strippedLabels });
        globalActions.setLabels(labels);
        globalActions.setProperties(file.properties);
        setDatasetFileVersion(file);
        setFileVersionLoading(false);
        setPdfFile(reformDatasetFileToPdfFile(file));
      }
    } else {
      setDatasetFileVersion(null);
      setFileVersionLoading(false);
      setPdfFile(null);
    }
  }, [fileId, globalActions, datasetFilesLength, history, projectId, datasetId]);

  useEffect(() => {
    if (fileId) {
      fetchData();
      globalActions.clearLabelHistory();
    }
  }, [fileId, fetchData, globalActions]);

  //FILTER & SORT

  const checkPagesAfterFilter = useCallback(() => {
    if (paginationObject) {
      const pageMinCapacity = (paginationObject.page - 1) * paginationObject.pageSize;
      if (filterItemsCount > 0 && filterItemsCount < pageMinCapacity) {
        setPaginationObject(prev => ({ ...(prev || defaultPaging), page: 1 }));
      }
    }
  }, [filterItemsCount, paginationObject]);

  useEffect(() => {
    setQueryString(renderFilterString(filters));
    checkPagesAfterFilter();
  }, [filters, checkPagesAfterFilter]);

  useEffect(() => {
    if (paginationObject) {
      history.replace({
        pathname: location.pathname,
        search: 'page=' + paginationObject.page + '&size=' + paginationObject.pageSize + queryString,
      });
    }
  }, [history, location.pathname, paginationObject, queryString]);

  useEffect(() => {
    const selectableLabels = datasetVersion?.settings.annotationTypes.filter(label => !label.hidden);
    globalActions.setLabelOptions(selectableLabels || []);
  }, [datasetVersion, globalActions]);

  useEffect(() => {
    if (datasetFiles.length === 0) {
      setDatasetFileVersion(null);
      setPdfFile(null);
    }
  }, [datasetFiles]);

  //OTHER FUNCTIONS

  const goBack = () => {
    history.push(
      `/projects/${projectId}/datasets/${datasetId}/overview?${
        paginationObject?.page ? `page=${paginationObject.page}` : ''
      }${queryString}`
    );
  };

  const switchToFileUrl = useCallback(
    (fileId: any) => {
      return `/projects/${projectId}/datasets/${datasetId}/dataset-file/${fileId}?${
        paginationObject?.page ? 'page=' + paginationObject.page : ''
      }${queryString}`;
    },
    [projectId, datasetId, paginationObject, queryString]
  );

  const { labels } = globalState;

  const filteredLabels = useMemo(() => labels.filter(label => label.exists || !label.reliable), [labels]);

  const onPropertiesChange = async (properties: {}) => {
    if (datasetFileVersion) {
      await setDatasetFileProperties(datasetFileVersion.id, properties);
      refetchFiles(true);
    }
  };

  const onAnnotationChange = async (newLabels: Label[]) => {
    if (datasetFileVersion) {
      const oldLabels = globalState.labels;
      const strippedLabels = transformLabels(newLabels);

      try {
        globalActions.setLabels(newLabels);
        let p = { tag: '', annotationState: 'NEW' };
        if (datasetFileVersion.properties) p = datasetFileVersion.properties;
        if (p.annotationState === 'NEW' || p.annotationState === 'SAVED') {
          p.annotationState = 'ONGOING';
          await setDatasetFileAnnotations(datasetFileVersion.id, { fields: strippedLabels });
          await setDatasetFileProperties(datasetFileVersion.id, p);
          refetchFiles(true);
        } else {
          await setDatasetFileAnnotations(datasetFileVersion.id, { fields: strippedLabels, properties: p });
        }
      } catch (e) {
        globalActions.setLabels(oldLabels);
      }
    }
  };

  return (
    <>
      {notOnLatestVersionAlert && !loading && !filesLoading ? (
        <div className="absolute z-10 top-12 left-0 right-0 flex justify-center">
          <Tag color="volcano" style={{ fontSize: '14px' }}>
            Attention! You are currently working within an older version of this dataset!
          </Tag>
        </div>
      ) : null}
      <TopBar
        title="Annotate Documents"
        closeFunction={(event: any) => {
          event.preventDefault();
          event.stopPropagation();
          goBack();
        }}
      />
      <DetailContainer>
        {paginationObject && (
          <DatasetMenuSideBar
            projectId={projectId}
            datasetVersion={datasetVersion}
            datasetFiles={datasetFiles}
            paginationObject={paginationObject}
            loading={filesLoading}
            setPaginationObject={setPaginationObject}
            filters={filters}
            setFilters={setFilters}
            filterItemsCount={filterItemsCount}
            selectedId={fileId}
            onClickUrl={(fileId: string) => {
              return switchToFileUrl(fileId);
            }}
            readOnly={globalState.readOnly}
          />
        )}
        <Layout>
          <Content>
            <div className={'pdf-layout'} style={{ width: '100%', height: '100%' }}>
              {fileVersionloading ? (
                <Spinner />
              ) : (
                <PdfContainer
                  file={pdfFile}
                  labels={filteredLabels.filter(label => !label.hidden)}
                  showNonExisting={true}
                  newLabelAllowed={true}
                  onAnnotationChange={onAnnotationChange}
                />
              )}
            </div>
          </Content>
        </Layout>
        <CustomSider width={400} border="left" style={{ paddingTop: '1.25rem' }}>
          {fileVersionloading ? (
            <Spinner />
          ) : datasetFileVersion && !fileVersionloading ? (
            <ResultSideBar
              datasetFileVersion={datasetFileVersion}
              onAnnotationChange={onAnnotationChange}
              onPropertiesChange={onPropertiesChange}
            />
          ) : (
            <Section>
              <span>No file selected</span>
            </Section>
          )}
        </CustomSider>
      </DetailContainer>
    </>
  );
};

export default withRouter(DatasetFileOverview);
